import { useUserStore } from '@/stores/userStore';
import { useAuthStore } from '@/stores/authStore';
import fhirDao from '@/helperScripts/fhirHelpers/dao/fhirDao';
import { UserRole } from '@/tsEnums/userRole';
import { KeycloakCatsUserRoles } from '@/tsEnums/KeycloakCatsUserRoles';

const getCurrentUserName = function () {
  const authStore = useAuthStore();
  return authStore.keycloak.idTokenParsed?.name;
};

const initUser = async function () {
  await getUserFhirId();
  setUserRole();
  setUsrPermissions();
  await updateUsrFavouriteStudies();
};

/**
 * Sets user permissions in store according to user role.
 */
const setUsrPermissions = function () {
  const userStore = useUserStore();

  userStore.usrPermissions = {
    viewTrials: false,
    changeTrialInfo: false,
    changeTrialInfoAdvanced: false,
    viewChangeHistory: false,
    confirmChanges: false,
    createTrials: false,
    deleteTrials: false,
  }; //Reset permissions
  switch (userStore.usrRole) {
    case 'Maintainer':
      userStore.usrPermissions.viewTrials = true;
      userStore.usrPermissions.changeTrialInfo = true;
      userStore.usrPermissions.createTrials = true;
      userStore.usrPermissions.viewChangeHistory = true;
      userStore.usrPermissions.confirmChanges = true;
      userStore.usrPermissions.changeTrialInfoAdvanced = true;
      userStore.usrPermissions.deleteTrials = true;
      break;
    case 'Reporter':
      userStore.usrPermissions.viewTrials = true;
      userStore.usrPermissions.changeTrialInfo = true;
      userStore.usrPermissions.createTrials = true;
      userStore.usrPermissions.viewChangeHistory = true;
      break;
    case 'Viewer':
      userStore.usrPermissions.viewTrials = true;
      break;
    case 'Patient':
      userStore.usrPermissions.viewTrials = true;
      break;

    default:
      userStore.usrPermissions.viewTrials = true;
      break;
  }
};

const getUserFhirId = async function () {
  const userStore = useUserStore();
  const authStore = useAuthStore();

  let userFhirRepresentation = await getUserByIdentifier(
    authStore.keycloak.subject
  );
  userStore.usrId = await extractUserId(userFhirRepresentation);
};

const isUserPatient = (authStore) => {
  return authStore.keycloak.realmAccess.roles.includes(
    KeycloakCatsUserRoles.Patient
  );
};

/**
 * checks a server responce if a FHIR Practitioner resource exists, creates a new one if necessary and gives the id of the practicioner
 * @param serverUserResponse responce from server for requesting a specific fhir practicioner with id
 * @returns the id of the practicioner resource
 */
const extractUserId = async function (serverUserResponse) {
  if (
    !serverUserResponse.data.entry ||
    serverUserResponse.data.entry.length === 0
  ) {
    const authStore = useAuthStore();
    // Need to create FHIR representation of user
    if (isUserPatient(authStore)) {
      createNewPatient(); //TODO Test this branch
    } else {
      serverUserResponse = await createNewPractitioner();
    }
    return serverUserResponse.id;
  }
  return serverUserResponse.data.entry[0].resource.id;
};

const getUserByIdentifier = async function (ident) {
  //TODO Test branches (patient vs practitioner)
  const authStore = useAuthStore();
  const requestUrl = `/${isUserPatient(authStore) ? 'Patient' : 'Practitioner'}/?identifier=${ident}`;
  const response = await fhirDao.getFhirResource(requestUrl, false);
  return response;
};

/**
 * creates a new FHIR Patient resource using information contained in the keycloak token.
 */
const createNewPatient = async function () {
  const authStore = useAuthStore();

  console.log('Creating new Patient resource');

  let newPatient = {
    resourceType: 'Patient',
    meta: {
      profile: [
        'http://molit.eu/fhir/trials/StructureDefinition/patient-cats-user',
      ],
    },
    text: {
      status: 'generated',
      div: `<div xmlns="http://www.w3.org/1999/xhtml"><p><b>Generated Narrative</b></p><p><b>identifier</b>: id: ${authStore.keycloak.subject}</p></div>`,
    },
    identifier: [
      {
        system: 'https://test1-framework-app.molit.eu/auth',
        value: authStore.keycloak.subject,
      },
    ],
    active: true,
  };
  const response = await fhirDao.postFhirResource('/Patient', newPatient);

  newPatient = response.data;
  return newPatient;
};

/**
 * creates a new FHIR Practitioner resource using information contained in the keycloak token.
 */
const createNewPractitioner = async function () {
  const authStore = useAuthStore();

  console.log('Creating new Practitioner');

  let newPractitioner = {
    resourceType: 'Practitioner',
    meta: {
      profile: [
        'http://molit.eu/fhir/trials/StructureDefinition/trial-management-user',
      ],
    },
    text: {
      status: 'generated',
      div: `<div xmlns="http://www.w3.org/1999/xhtml"><p><b>Generated Narrative</b></p><p><b>identifier</b>: id: ${authStore.keycloak.subject}</p></div>`,
    },
    identifier: [
      {
        system: 'https://test1-framework-app.molit.eu/auth',
        value: authStore.keycloak.subject,
      },
    ],
    active: true,
  };
  const response = await fhirDao.postFhirResource(
    '/Practitioner',
    newPractitioner
  );

  newPractitioner = response.data;
  return newPractitioner;
};

const setUserRole = function () {
  const userStore = useUserStore();
  const authStore = useAuthStore();

  //Extracts relevant role from keycloak roles
  if (
    authStore.keycloak.realmAccess.roles.includes(
      KeycloakCatsUserRoles.Maintainer
    )
  ) {
    userStore.usrRole = UserRole.Maintainer;
  } else if (
    authStore.keycloak.realmAccess.roles.includes(
      KeycloakCatsUserRoles.Reporter
    )
  ) {
    userStore.usrRole = UserRole.Reporter;
  } else if (
    authStore.keycloak.realmAccess.roles.includes(KeycloakCatsUserRoles.Patient)
  ) {
    userStore.usrRole = UserRole.Patient;
  } else {
    userStore.usrRole = UserRole.Viewer;
  }
};

const updateUsrFavouriteStudies = async function () {
  const userStore = useUserStore();

  if (userStore.usrId != '' && userStore.usrId != undefined) {
    const response = await fhirDao.getFhirResource(
      '/List?source=' + userStore.usrId,
      false
    );
    if (response.status == 200) {
      userStore.usrFavouriteStudies = await extractUsrFavoriteStudies(response);
    }
  }
};

/**
 * checks a server responce if FHIR list resource exists for a specific user and creates a new one if necessary
 * @param serverResponceList responce from server for requesting the resource corresponding to a user ID
 * @returns the list resource
 */
const extractUsrFavoriteStudies = async function (serverResponceList) {
  if (
    !serverResponceList.data.entry ||
    serverResponceList.data.entry.length === 0
  ) {
    //no list found -> create new one
    console.log('No List found for current user. Server response was: ');
    console.log(serverResponceList);

    const responseNewList = await postNewPersonalStudyList();

    console.log('Created new favourite List');
    // userStore.usrFavouriteStudies.id = response.data.id; //Update local list with id from server
    return responseNewList.data;
  }
  //Matching resource found
  return serverResponceList.data.entry[0].resource; //Only considers first entry
};

/**
 *
 * @param {UserRole | string} role
 *
 * @returns {"Practitioner" | "Patient"} Fhir resource type corresponding to user role
 */
const getFhirResourceTypeForUserRole = (role) => {
  if (
    role === UserRole.Maintainer ||
    role === UserRole.Reporter ||
    role === UserRole.Viewer
  )
    return 'Practitioner';
  else return 'Patient';
};

const postNewPersonalStudyList = async function () {
  const userStore = useUserStore();
  const authStore = useAuthStore();

  let newList = {
    resourceType: 'List',
    meta: {
      profile: [
        'http://molit.eu/fhir/trials/StructureDefinition/FavouriteTrialsList',
      ],
    },
    text: {
      status: 'generated',
      div: `<div xmlns="http://www.w3.org/1999/xhtml"><p><b>Generated Narrative</b></p><p><b>user</b> identifier: ${authStore.keycloak.subject}</p></div>`,
    },
    status: 'current',
    mode: 'working',
    source: {
      reference:
        getFhirResourceTypeForUserRole(userStore.usrRole) +
        '/' +
        userStore.usrId,
    },
    entry: [],
  };
  return fhirDao.postFhirResource('/List', newList);
};

const addStudyToPersonalList = async function (studyId) {
  const userStore = useUserStore();

  //Change list locally
  let newRefObj = {
    item: {
      reference: 'ResearchStudy/' + studyId,
    },
  };
  if (!userStore.usrFavouriteStudies.entry) {
    userStore.usrFavouriteStudies.entry = [];
  }
  userStore.usrFavouriteStudies.entry.push(newRefObj);
  //Update on server and replace local list with new resource from server
  const response = await putPersonalStudyListToServer();
  userStore.usrFavouriteStudies = response.data;
};

const putPersonalStudyListToServer = function () {
  const userStore = useUserStore();

  return fhirDao.putFhirResource(
    '/List/' + userStore.usrFavouriteStudies.id,
    userStore.usrFavouriteStudies
  );
};

const removeStudyFromPersonalList = async function (studyId) {
  const userStore = useUserStore();

  //Change list locally
  let i = 0;
  let found = false;
  while (i < userStore.usrFavouriteStudies.entry.length && !found) {
    const currEl = userStore.usrFavouriteStudies.entry[i];
    if (currEl.item.reference === 'ResearchStudy/' + studyId) {
      found = true;
    } else {
      i++;
    }
  }
  if (found) {
    userStore.usrFavouriteStudies.entry.splice(i, 1); //Remove reference to study
    //Update on server and replace local list with new resource from server
    const response = await putPersonalStudyListToServer();
    userStore.usrFavouriteStudies = response.data;
  } else {
    throw new Error('Entry to be deleted was not found.');
  }
};

/**
 * @returns true if study with @param id is in user's list of favourite studies, represented by @param userFavStudyList.
 */
const studyInGivenList = function (id, userFavStudyList) {
  if (userFavStudyList.entry) {
    let found = false;
    let i = 0;
    while (!found && i < userFavStudyList.entry.length) {
      const e = userFavStudyList.entry[i];
      if (e.item.reference == 'ResearchStudy/' + id) {
        found = true;
      } else {
        i++;
      }
    }
    return found;
  } else {
    return false;
  }
};

export default {
  addStudyToPersonalList,
  createNewPractitioner,
  getPractitionerByIdentifier: getUserByIdentifier,
  getUserFhirId,
  initUser,
  postNewPersonalStudyList,
  putPersonalStudyListToServer,
  removeStudyFromPersonalList,
  studyInGivenList,
  getCurrentUserName,
  extractPracticionerFhirID: extractUserId,
  extractUsrFavoriteStudies,
  isUserPatient,
  getFhirResourceTypeForUserRole,
};
